<script setup lang="ts">
import { computed } from 'vue';
import { NIcon, NTag } from 'naive-ui';
import { IconDocument } from '@/components/design/icons';

/**
 * Presentational card for a human-in-the-loop action that needs the
 * user's approval. It owns the visual shell only — the header ("ACTION REQUIRED
 * · <TYPE>" + status), the bold title, a clean key/value detail table, and the
 * footer action buttons — and is deliberately payload-agnostic so any human-in-the-loop flow
 * can drive it. Callers are responsible for what goes in `details` (in
 * particular: never pass raw UIDs — see the interrupt panel's UID filtering).
 */
export type HumanInTheLoopStatus = 'pending' | 'submitting' | 'resolved';

/**
 * What the card is asking for. `approval` is the original gate-before-execution
 * wording; `question` is the model asking the user something it can't decide
 * itself, where "Pending approval"/"Approved" would read as the wrong promise.
 */
export type HumanInTheLoopVariant = 'approval' | 'question';

export interface HumanInTheLoopDetailFile {
  name: string;
  ext: string | null;
}

/**
 * A detail's value is structured (not a pre-stringified blob) so the card can
 * render lists and files in a human-friendly way instead of dumping JSON.
 */
export type HumanInTheLoopDetailValue =
  | { kind: 'text'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'files'; files: HumanInTheLoopDetailFile[] };

export interface HumanInTheLoopDetail {
  label: string;
  value: HumanInTheLoopDetailValue;
}

export interface HumanInTheLoopAction {
  key: string;
  label: string;
  variant?: 'primary' | 'ghost';
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    title: string;
    status?: HumanInTheLoopStatus;
    variant?: HumanInTheLoopVariant;
    details?: HumanInTheLoopDetail[];
    actions?: HumanInTheLoopAction[];
  }>(),
  {
    status: 'pending',
    variant: 'approval',
    details: () => [],
    actions: () => [],
  }
);

const emit = defineEmits<{
  action: [key: string];
}>();

const eyebrowLabel = computed(() =>
  props.variant === 'question' ? 'Question' : 'Action required'
);

const statusLabel = computed(() => {
  const isQuestion = props.variant === 'question';
  switch (props.status) {
    case 'submitting':
      return isQuestion ? 'Sending…' : 'Submitting…';
    case 'resolved':
      return isQuestion ? 'Answered' : 'Approved';
    default:
      return isQuestion ? 'Waiting for your answer' : 'Pending approval';
  }
});

function onAction(action: HumanInTheLoopAction): void {
  if (action.disabled) return;
  emit('action', action.key);
}
</script>

<template>
  <section class="human-in-the-loop-card" :class="`human-in-the-loop-card--${status}`">
    <header class="human-in-the-loop-card__header">
      <div class="human-in-the-loop-card__eyebrow">
        <svg class="human-in-the-loop-card__check" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M8.5 13.6 5 10.1l1.2-1.2 2.3 2.3 5-5L16.8 7.4z" fill="currentColor" />
        </svg>
        <span class="human-in-the-loop-card__eyebrow-label">{{ eyebrowLabel }}</span>
      </div>
      <span class="human-in-the-loop-card__status">
        <span class="human-in-the-loop-card__status-dot" aria-hidden="true" />
        {{ statusLabel }}
      </span>
    </header>

    <div class="human-in-the-loop-card__body">
      <h3 class="human-in-the-loop-card__title">{{ title }}</h3>

      <dl v-if="details.length > 0" class="human-in-the-loop-card__details">
        <div v-for="detail in details" :key="detail.label" class="human-in-the-loop-card__row">
          <dt class="human-in-the-loop-card__label">
            {{ detail.label }}
            <span v-if="detail.value.kind === 'files'" class="human-in-the-loop-card__count">
              {{ detail.value.files.length }}
              {{ detail.value.files.length === 1 ? 'file' : 'files' }}
            </span>
          </dt>
          <dd class="human-in-the-loop-card__value">
            <ul v-if="detail.value.kind === 'files'" class="human-in-the-loop-card__files">
              <li
                v-for="(file, index) in detail.value.files"
                :key="`${file.name}-${index}`"
                class="human-in-the-loop-card__file"
              >
                <NIcon class="human-in-the-loop-card__file-icon" :component="IconDocument" />
                <span class="human-in-the-loop-card__file-name">{{ file.name }}</span>
                <NTag v-if="file.ext" size="small" :bordered="false">{{ file.ext }}</NTag>
              </li>
            </ul>

            <ul v-else-if="detail.value.kind === 'list'" class="human-in-the-loop-card__list">
              <li
                v-for="(item, index) in detail.value.items"
                :key="`${item}-${index}`"
                class="human-in-the-loop-card__list-item"
              >
                {{ item }}
              </li>
            </ul>

            <template v-else>{{ detail.value.text }}</template>
          </dd>
        </div>
      </dl>

      <slot />
    </div>

    <footer v-if="actions.length > 0" class="human-in-the-loop-card__footer">
      <button
        v-for="action in actions"
        :key="action.key"
        type="button"
        class="human-in-the-loop-card__btn"
        :class="`human-in-the-loop-card__btn--${action.variant ?? 'ghost'}`"
        :disabled="action.disabled"
        @click="onAction(action)"
      >
        {{ action.label }}
      </button>
    </footer>
  </section>
</template>

<style scoped>
.human-in-the-loop-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

html.dark .human-in-the-loop-card {
  border-color: rgba(148, 163, 184, 0.24);
  background: rgba(15, 23, 42, 0.6);
}

.human-in-the-loop-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #eef2f6;
  background: #f8fafc;
}

html.dark .human-in-the-loop-card__header {
  border-bottom-color: rgba(148, 163, 184, 0.16);
  background: rgba(30, 41, 59, 0.5);
}

.human-in-the-loop-card__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #475569;
  min-width: 0;
}

html.dark .human-in-the-loop-card__eyebrow {
  color: #cbd5e1;
}

.human-in-the-loop-card__check {
  width: 13px;
  height: 13px;
  flex: none;
  color: #2563eb;
}

html.dark .human-in-the-loop-card__check {
  color: #60a5fa;
}

.human-in-the-loop-card__status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: none;
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
}

html.dark .human-in-the-loop-card__status {
  color: #94a3b8;
}

.human-in-the-loop-card__status-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #f59e0b;
}

.human-in-the-loop-card--submitting .human-in-the-loop-card__status-dot {
  background: #2563eb;
}

.human-in-the-loop-card--resolved .human-in-the-loop-card__status-dot {
  background: #16a34a;
}

.human-in-the-loop-card__body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.human-in-the-loop-card__title {
  margin: 0;
  font-size: inherit;
  font-weight: 700;
  line-height: 1.4;
  color: #0f172a;
  white-space: pre-wrap;
}

html.dark .human-in-the-loop-card__title {
  color: #e2e8f0;
}

.human-in-the-loop-card__details {
  margin: 0;
  border: 1px solid #eef2f6;
  border-radius: 10px;
  overflow: hidden;
}

html.dark .human-in-the-loop-card__details {
  border-color: rgba(148, 163, 184, 0.16);
}

.human-in-the-loop-card__row {
  display: grid;
  grid-template-columns: minmax(120px, 34%) 1fr;
  gap: 16px;
  padding: 12px 14px;
}

.human-in-the-loop-card__row + .human-in-the-loop-card__row {
  border-top: 1px solid #f1f5f9;
}

html.dark .human-in-the-loop-card__row + .human-in-the-loop-card__row {
  border-top-color: rgba(148, 163, 184, 0.12);
}

.human-in-the-loop-card__label {
  margin: 0;
  font-size: inherit;
  font-weight: 500;
  color: #64748b;
}

html.dark .human-in-the-loop-card__label {
  color: #94a3b8;
}

.human-in-the-loop-card__value {
  margin: 0;
  font-size: inherit;
  color: #0f172a;
  overflow-wrap: anywhere;
}

html.dark .human-in-the-loop-card__value {
  color: #e2e8f0;
}

.human-in-the-loop-card__count {
  margin-left: 6px;
  font-size: 0.85em;
  font-weight: 400;
  color: #94a3b8;
}

.human-in-the-loop-card__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.human-in-the-loop-card__files {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.human-in-the-loop-card__file {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.human-in-the-loop-card__file-icon {
  flex: none;
  color: #64748b;
}

html.dark .human-in-the-loop-card__file-icon {
  color: #94a3b8;
}

.human-in-the-loop-card__file-name {
  min-width: 0;
  overflow-wrap: anywhere;
}

.human-in-the-loop-card__footer {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0 16px 16px;
}

.human-in-the-loop-card__btn {
  padding: 8px 18px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: inherit;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    opacity 0.15s ease;
}

.human-in-the-loop-card__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.human-in-the-loop-card__btn--primary {
  background: #0f172a;
  border-color: #0f172a;
  color: #ffffff;
}

.human-in-the-loop-card__btn--primary:not(:disabled):hover {
  background: #1e293b;
  border-color: #1e293b;
}

html.dark .human-in-the-loop-card__btn--primary {
  background: #f8fafc;
  border-color: #f8fafc;
  color: #0f172a;
}

html.dark .human-in-the-loop-card__btn--primary:not(:disabled):hover {
  background: #e2e8f0;
  border-color: #e2e8f0;
}

.human-in-the-loop-card__btn--ghost {
  background: transparent;
  border-color: #cbd5e1;
  color: #0f172a;
}

.human-in-the-loop-card__btn--ghost:not(:disabled):hover {
  background: #f1f5f9;
}

html.dark .human-in-the-loop-card__btn--ghost {
  border-color: rgba(148, 163, 184, 0.4);
  color: #e2e8f0;
}

html.dark .human-in-the-loop-card__btn--ghost:not(:disabled):hover {
  background: rgba(148, 163, 184, 0.12);
}
</style>
